import { has, omit } from "lodash";
import { from, Observable } from "rxjs";
import { store } from "..";
import { withProgress } from "../../components/loader/file-progress";
import { ResultWithProgress } from "../../components/loader/models";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { addFieldsToForm, flattenObj } from "../common/forms";
import { processErrorFromPromise } from "../errors/utils";
import { successNotificationFromPromise } from "../notifications/utils";
import { LaunchConfigurationsActions } from "./actions";
import { LaunchConfigurationsDTO } from "./models/dto";

export namespace LaunchConfigurationsService {
  const api = new API("kyc-upload");

  export function getLaunchConfigurations(
    pagination: Pageable.T<
      keyof LaunchConfigurationsDTO.Responses.GetSingle.T
    > = {}
  ): Observable<
    | FetchableError
    | FetchableSuccess<LaunchConfigurationsDTO.Responses.GetAll.T>
  > {
    return from(
      api
        .get(`ui/configurations`)
        .query(Pageable.getQueryParam(omit(pagination, "filters")))
        .then<FetchableSuccess<LaunchConfigurationsDTO.Responses.GetAll.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getLaunchConfiguration(
    id: LaunchConfigurationsDTO.Responses.GetSingle.T["id"]
  ): Promise<LaunchConfigurationsDTO.Responses.GetSingle.T> {
    const { launchConfigurationMap } = store.getState().launchConfigurations;

    if (
      has(launchConfigurationMap, id) &&
      launchConfigurationMap[id] !== "LOADING"
    ) {
      return Promise.resolve(
        //TOOD fix for concurrency
        launchConfigurationMap[
          id
        ] as LaunchConfigurationsDTO.Responses.GetSingle.T
      );
    }

    store.dispatch(
      LaunchConfigurationsActions.getSingleLaunchConfiguration(id)
    );
    return api
      .get(`ui/configurations/${id}`)
      .then<LaunchConfigurationsDTO.Responses.GetSingle.T>((response) => {
        store.dispatch(
          LaunchConfigurationsActions.setSingleLaunchConfiguration({
            id,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<LaunchConfigurationsDTO.Responses.GetSingle.T>(
        processErrorFromPromise
      );
  }

  export function removeLaunchConfiguration(
    id: LaunchConfigurationsDTO.Responses.GetSingle.T["id"]
  ): Promise<void> {
    return api
      .delete(`ui/configurations/${id}`)
      .then(successNotificationFromPromise("launch-configuration-removed"))
      .then(() => {
        store.dispatch(LaunchConfigurationsActions.resetLaunchConfigurations());
      })
      .catch<void>(processErrorFromPromise);
  }

  export function updateLaunchConfiguration(
    id: LaunchConfigurationsDTO.Responses.GetSingle.T["id"],
    data: LaunchConfigurationsDTO.Requests.SetSingle.T
  ): Promise<{ id: string }> {
    return api
      .put(`ui/configurations/${id}`)
      .send(data)
      .then<{ id: string }>((res) => {
        store.dispatch(LaunchConfigurationsActions.resetLaunchConfigurations());
        return res.body;
      })
      .then(
        successNotificationFromPromise("launch-configuration-has-been-updated")
      )
      .catch<{ id: string }>(processErrorFromPromise);
  }

  export function postLaunchConfiguration(
    data: LaunchConfigurationsDTO.Requests.SetSingle.T
  ): Promise<{ id: string }> {
    return api
      .post(`ui/configurations/`)
      .send(data)
      .then<{ id: string }>((res) => {
        store.dispatch(LaunchConfigurationsActions.resetLaunchConfigurations());
        return res.body;
      })
      .then(
        successNotificationFromPromise("launch-configuration-has-been-created")
      )
      .catch<{ id: string }>(processErrorFromPromise);
  }

  export function addHeaderImage(
    configId: string,
    fields: LaunchConfigurationsDTO.Requests.AddImage.T
  ): Observable<
    ResultWithProgress<LaunchConfigurationsDTO.Responses.AddImage.T>
  > {
    return withProgress(
      addFieldsToForm(
        api.post(`ui/configurations/${configId}/images`),
        flattenObj(fields)
      ),
      (r) =>
        r
          .responseType("json")
          .then(successNotificationFromPromise("launch-config-image-added"))
          .then((response) => {
            store.dispatch(
              LaunchConfigurationsActions.resetLaunchConfigurations()
            );

            return response.body;
          })
          .catch<LaunchConfigurationsDTO.Responses.AddImage.T>(
            processErrorFromPromise
          )
    );
  }
}
